import { Inject, Injectable } from '@angular/core';
import { FirebaseApp } from 'firebase/app';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import {
  getUserSettingsReportsCollectionPath,
  getUserSettingsReportsDocumentId,
  ReportDefaultSettings,
  ReportDefaultSettingsKeys,
} from 'ag-common-lib/lib/models/reporting/report-default-settings.model';
import { type ReportId } from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { FIREBASE_APP } from '../injections/firebase-app';
import { DataService } from './data.service';

@Injectable({ providedIn: 'root' })
export class ReportDefaultSettingsService extends DataService<ReportDefaultSettings> {
  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    super(fireBaseApp);
  }

  getByReportId = async (
    reportId: ReportId,
    options: { isPublic?: boolean } = {},
  ): Promise<ReportDefaultSettings | null> => {
    if (options.isPublic !== undefined) {
      return this.getFromPath(reportId, options.isPublic);
    }

    const privateSettings = await this.getFromPath(reportId, false);
    if (privateSettings) {
      return privateSettings;
    }

    return this.getFromPath(reportId, true);
  };

  save = async (settings: ReportDefaultSettings): Promise<ReportDefaultSettings> => {
    const reportId = settings[ReportDefaultSettingsKeys.reportId];
    const isPublic = Boolean(settings[ReportDefaultSettingsKeys.isPublic]);
    const documentId = getUserSettingsReportsDocumentId(isPublic);
    const payload: ReportDefaultSettings = {
      ...settings,
      [BaseModelKeys.dbId]: documentId,
      [ReportDefaultSettingsKeys.isPublic]: isPublic,
    };

    this.setReportsCollection(reportId);

    const existing = await this.getById(documentId);
    if (existing) {
      const saved = await this.updateFields(documentId, payload);
      await this.deleteFromOppositePath(reportId, isPublic);
      return saved;
    }

    const saved = await this.create(payload, documentId);
    await this.deleteFromOppositePath(reportId, isPublic);
    return saved;
  };

  private getFromPath = async (reportId: ReportId, isPublic: boolean): Promise<ReportDefaultSettings | null> => {
    this.setReportsCollection(reportId);
    return this.getById(getUserSettingsReportsDocumentId(isPublic));
  };

  private deleteFromOppositePath = async (reportId: ReportId, isPublic: boolean): Promise<void> => {
    this.setReportsCollection(reportId);
    const oppositeDocumentId = getUserSettingsReportsDocumentId(!isPublic);
    const opposite = await this.getById(oppositeDocumentId);
    if (!opposite) {
      return;
    }

    await this.delete(oppositeDocumentId);
  };

  private setReportsCollection(reportId: ReportId): void {
    this.setCollection(getUserSettingsReportsCollectionPath(reportId));
  }
}
